import type { PuzzleDocument, PuzzleSize, PuzzleVariant } from '../../domain/puzzle';
import { useI18n } from '../../i18n';

interface PuzzleSetupPanelProps {
  puzzle: PuzzleDocument;
  solutionLimit: number;
  maxAddedBlueCircles: number;
  onVariantChange: (variant: PuzzleVariant) => void;
  onSizeChange: (size: PuzzleSize) => void;
  onNameChange: (name: string) => void;
  onDateChange: (date: string) => void;
  onSolutionLimitChange: (limit: number) => void;
  onMaxAddedBlueCirclesChange: (limit: number) => void;
}

export default function PuzzleSetupPanel({
  puzzle,
  solutionLimit,
  maxAddedBlueCircles,
  onVariantChange,
  onSizeChange,
  onNameChange,
  onDateChange,
  onSolutionLimitChange,
  onMaxAddedBlueCirclesChange,
}: PuzzleSetupPanelProps) {
  const { copy } = useI18n();

  return (
    <div className="setup-grid">
      <div className="setup-group">
        <label className="field-label">{copy.puzzleSetup.nameLabel}</label>
        <input
          className="text-input"
          type="text"
          value={puzzle.name}
          onChange={(event) => onNameChange(event.target.value)}
          placeholder={copy.puzzleSetup.namePlaceholder}
        />
      </div>

      <div className="setup-group">
        <label className="field-label">{copy.puzzleSetup.dateLabel}</label>
        <input
          className="text-input"
          type="date"
          value={puzzle.created_at}
          onChange={(event) => onDateChange(event.target.value)}
        />
      </div>

      <div className="setup-group">
        <label className="field-label">{copy.puzzleSetup.variantLabel}</label>
        <div className="segmented-control">
          {(['classic', 'consecutive'] as const).map((variant) => (
            <button
              key={variant}
              type="button"
              className={variant === puzzle.variant ? 'segment active' : 'segment'}
              onClick={() => onVariantChange(variant)}
            >
              {variant === 'classic' ? copy.puzzleSetup.classicVariant : copy.puzzleSetup.consecutiveVariant}
            </button>
          ))}
        </div>
      </div>

      <div className="setup-group">
        <label className="field-label">{copy.puzzleSetup.gridSizeLabel}</label>
        <div className="segmented-control">
          {([6, 9] as const).map((size) => (
            <button
              key={size}
              type="button"
              className={size === puzzle.size ? 'segment active' : 'segment'}
              onClick={() => onSizeChange(size)}
            >
              {size}x{size}
            </button>
          ))}
        </div>
      </div>

      <div className="setup-group">
        <label className="field-label">{copy.puzzleSetup.solutionLimitLabel}</label>
        <input
          className="text-input"
          type="number"
          min={1}
          max={144}
          value={solutionLimit}
          onChange={(event) => {
            const parsed = Number.parseInt(event.target.value, 10);
            onSolutionLimitChange(Number.isNaN(parsed) ? 1 : Math.max(1, Math.min(parsed, 144)));
          }}
        />
      </div>

      {puzzle.variant === 'consecutive' ? (
        <div className="setup-group">
          <label className="field-label">{copy.puzzleSetup.maxBlueCirclesLabel}</label>
          <input
            className="text-input"
            type="number"
            min={0}
            max={144}
            value={maxAddedBlueCircles}
            onChange={(event) => {
              const parsed = Number.parseInt(event.target.value, 10);
              onMaxAddedBlueCirclesChange(Number.isNaN(parsed) ? 0 : Math.max(0, Math.min(parsed, 144)));
            }}
          />
        </div>
      ) : null}
    </div>
  );
}
